/**
 * The one row recipe a list card's consumers use (spec 226, contracts/pieces.md):
 * the Members row's measured classes, named so the later lists (228 Labels and
 * Images, 229 Connections, Triggers and Secrets, 230 Used by and By workflow)
 * import them instead of spelling them again.
 *
 * Class strings, not a component: the wrapping `<ul aria-label>` or the
 * day-grouped `<ol>`s are the consumer's, so semantics stay with the feature.
 * Consumers add state classes (arrival slide, tint) through `cn()` and never
 * change the padding or the type sizes.
 *
 * Row height on a coarse pointer comes from `py-2.5` plus a 24 px line and the
 * avatar or icon: no `min-h`, which would override the `touch-target` floor of
 * any control inside (memory: touch-target loses to min-h).
 */

/** The `<ul>`/`<ol>` around rows: hairline dividers, no outer border. */
export const LIST_ROWS = 'divide-border flex flex-col divide-y';

/**
 * One row: bleeds 8px into the card padding so a hover or arrival tint reaches
 * the card edge. Square on a phone, where there is no hover: there the
 * divider above a rounded row bent up at both ends (spec 276).
 */
export const LIST_ROW =
  '-mx-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md px-2 py-2.5 max-md:rounded-none';

/** The row's name line. */
export const LIST_ROW_NAME = 'text-foreground truncate text-sm font-medium';

/** The row's secondary lines: an email, a date, a sentence's context. */
/**
 * A name that opens its thing (spec 234 R6): the row name's colour and weight,
 * underlined on hover, with the app's focus ring. Primary blue stays for actions.
 */
export const LIST_ROW_LINK =
  'text-foreground focus-visible:ring-ring rounded-sm font-medium break-words underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:outline-none';

export const LIST_ROW_SECONDARY = 'text-muted-foreground truncate text-xs';

/** The right-hand slot (chip, role, menu): never shrinks, stays on one line. */
export const LIST_ROW_TRAILING = 'ms-auto flex shrink-0 items-center gap-2';

/**
 * A row whose second line wraps on a phone (docs/phone.md PH-06, spec 279). The
 * mark, the name and the trailing actions share the first line, and the second
 * line spans the width under the name and the actions. With the actions centred
 * down the side instead, a "Connect" or a "⋯" took 90px from every line of a
 * description and sat beside its middle line rather than its name.
 *
 * `LIST_ROW_PHONE_GRID` goes on the row, `LIST_ROW_PHONE_TEXT` on the column that
 * holds the two lines, and the other three on the name, the second line and the
 * trailing actions. The mark needs nothing: it takes the first free cell.
 */
export const LIST_ROW_PHONE_GRID =
  'max-md:grid max-md:grid-cols-[auto_minmax(0,1fr)_auto] max-md:gap-y-0.5';

export const LIST_ROW_PHONE_TEXT = 'max-md:contents';

export const LIST_ROW_PHONE_NAME = 'max-md:col-start-2 max-md:row-start-1';

export const LIST_ROW_PHONE_SECOND =
  'max-md:col-span-2 max-md:col-start-2 max-md:row-start-2';

// The actions' 44px reach overhangs the name's line by 10px each way, so the
// second line sits under the name, not under a 44px line.
export const LIST_ROW_PHONE_END =
  'max-md:col-start-3 max-md:row-start-1 max-md:-my-2.5';
